import { Link } from '@tanstack/react-router';
import { FormattedMessage } from 'react-intl';

import type { TranslationKeys } from '@/lib/translations/types';
import { cn } from '@/lib/utils';
import type { FileRouteTypes } from '@/routeTree.gen';

interface Props {
  title: TranslationKeys;
  requirements: {
    id: string;
    logoUrl: string;
    ok: boolean;
    onClick?: () => void;
    to?: FileRouteTypes['to'];
    available: boolean;
  }[];
  onClick?: () => void;
}

const ExampleContent = ({
  requirements,
  title,
}: Pick<Props, 'requirements' | 'title'>): JSX.Element => (
  <div
    className={cn(
      'rounded-sm w-full flex flex-row justify-between items-center p-2 border w-full gap-1',
      requirements.every(({ ok }) => ok)
        ? 'border-green-500 hover:bg-gray-100 dark:hover:bg-muted'
        : 'border-red-500 cursor-default',
    )}
  >
    <div className="text-sm text-left grow basis-0">
      {requirements.some(({ available }) => !available) ? '[Coming soon] ' : ''}
      <FormattedMessage id={title} />
    </div>
    <div className="flex flex-col md:flex-row gap-1 items-end md:items-center">
      {requirements.map(({ ok, logoUrl, onClick, id, available, to }) =>
        ok ? (
          <div key={id} className="px-1 py-1 dark:bg-gray-200 rounded-sm">
            <img
              src={logoUrl}
              className="w-[20px] h-[20px]"
              alt={id}
              key={id}
            />
          </div>
        ) : to === undefined ? (
          <button
            onClick={onClick}
            key={id}
            className="flex flex-row items-center text-xs border px-2 py-1 rounded-sm hover:bg-muted border-gray-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 dark:disabled:bg-background dark:disabled:text-muted dark:disabled:border-muted"
            disabled={!available}
          >
            <FormattedMessage id="integrations.tryIt.connect" />
            <img src={logoUrl} className="w-[20px] h-[20px] ml-2" alt={id} />
          </button>
        ) : (
          <Link to={to} key={id}>
            <button
              className="flex flex-row items-center text-xs border px-2 py-1 rounded-sm hover:bg-muted border-gray-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 dark:disabled:bg-background dark:disabled:text-muted dark:disabled:border-muted"
              disabled={!available}
            >
              <FormattedMessage id="integrations.tryIt.connect" />
              <img
                src={logoUrl}
                className="w-[20px] h-[20px] ml-2"
                alt={id}
                key={id}
              />
            </button>
          </Link>
        ),
      )}
    </div>
  </div>
);

export const Example = ({
  requirements,
  title,
  onClick,
}: Props): JSX.Element => {
  if (requirements.every(({ ok }) => ok)) {
    return (
      <div className="grow">
        <button onClick={onClick} className="w-full">
          <ExampleContent requirements={requirements} title={title} />
        </button>
      </div>
    );
  }

  return (
    <div className="grow">
      <ExampleContent requirements={requirements} title={title} />
    </div>
  );
};
